<script setup lang="ts">
import { defineAsyncComponent } from 'vue';

// Lazy load de modais para reduzir Script Evaluation inicial
const BaseModalGlobal = defineAsyncComponent(
  () => import('@/components/base/modal/Global.vue'),
);

const BaseModalVerification = defineAsyncComponent(
  () => import('@/components/base/modal/Verification.vue'),
);

const BaseModalLegalAge = defineAsyncComponent(
  () => import('@/components/base/modal/LegalAge.vue'),
);

const UserAccountSecurityModalPassword = defineAsyncComponent(
  () => import('@/components/user/account/security/ModalPassword.vue'),
);

// Lazy load de widgets condicionais
const WidgetsCookieConsents = defineAsyncComponent(
  () => import('@/components/widgets/CookieConsents.vue'),
);

const WidgetsInstallAppPopup = defineAsyncComponent(
  () => import('@/components/widgets/InstallAppPopup.vue'),
);

const WidgetsPwaUpdate = defineAsyncComponent(
  () => import('@/components/widgets/PwaUpdate.vue'),
);

const WidgetsSupportButton = defineAsyncComponent(
  () => import('@/components/widgets/SupportButton.vue'),
);

// Lazy load da Sidebar V2 - só carrega quando necessário
const Sidebar = defineAsyncComponent(
  () => import('@/components/sidebarv2/Index.vue'),
);

// Lazy load do CashbackMenu para uso global no mobile
const CashbackMenu = defineAsyncComponent(
  () => import('@/components/sidebar/menu/CashbackMenu.vue'),
);

// Lazy load do modal Giratudo
const ApostatudoGiratudoModal = defineAsyncComponent(
  () => import('@/components/apostatudo/organisms/GiratudoModal.vue'),
);

// Lazy load do modal da Roletinha (bottom-sheet global, abre via store)
const RouletteGame = defineAsyncComponent(
  () => import('@/components/roulette/RouletteGame.vue'),
);

// Lazy load do hub Club Fidelidade (bottom-sheet global, abre via store)
const LoyaltyHub = defineAsyncComponent(
  () => import('@/components/loyalty-hub/LoyaltyHub.vue'),
);

// Bottom sheet de preview do jogo (mobile/tablet apenas)
const CasinoGameMobilePreview = defineAsyncComponent(
  () => import('@/components/casino/game/MobilePreview.vue'),
);

const layoutStore = useLayoutStore();
const { menuOpen, isCashbackMenuOpen } = storeToRefs(layoutStore);
const { isClubFidelidadeEnabled } = useFeatureFlagsJson();
const loyaltyHubStore = useLoyaltyHubStore();

// Grafias aceitas do deep-link do hub Club Fidelidade. Canônico p/ divulgar:
// `?open=clube-fidelidade`; as outras funcionam como alias por conveniência.
const LOYALTY_HUB_QUERY_VALUES = [
  'clube-fidelidade',
  'clube-de-fidelidade',
  'club-fidelidade',
];

// Pull-to-refresh: so dispara dentro do WebView nativo; em navegador o pull
// nativo do Chrome/Safari continua atuando. Detalhes em `usePullToRefresh`.
const { pullDistance, isTriggered, isReloading, maxDisplay } =
  usePullToRefresh();

const casinoStore = useCasinoStore();
const { isMobile, isLayoutMobileOrTablet } = useLayout();
const { userCanOnlyWithdraw } = useUserLimits();
const { isSportsPage } = useSportsBook();
const { isPokerPage } = usePoker();

const { isTopbarUpToFiveItemsEnabled } = useFeatureFlagsNew();
const { isDesktop } = useLayout();
const { isBot } = usePerformanceBotBypass();

// Reporta a cor do topo pro app nativo pintar a faixa de safe-area (notch).
useNativeSafeAreaColor();

// Cashback menu global: trata tanto o deep-link ?open=cashback quanto
// pedidos via openCashbackMenu() (ex.: MenuHorizontalApostatudo).
// Centralizado aqui pra funcionar independente do estado da sidebar
// (lazy-load, tab ativa, etc.).
const { hasRouteParam, shouldOpenCashback, consumeOpenRequest } =
  useCashbackMenu();

const showGlobalCashbackModal = ref(false);
const globalCashbackStyle = ref<Record<string, string> | undefined>(undefined);

// Calcula a posição do dropdown de cashback no desktop, ancorado no botão
// da sidebar ([data-cashback-btn]). Mesmo cálculo que o CasinoItems usa
// pra o dropdown disparado por clique, replicado aqui pra que o deep-link
// ?open=cashback funcione antes/durante o lazy-load da sidebar.
// Faz retry porque a sidebar é async (defineAsyncComponent): o botão
// pode não existir nos primeiros frames depois que o modal abre.
function computeGlobalCashbackStyle(retries = 10) {
  if (!isDesktop.value) {
    globalCashbackStyle.value = undefined;

    return;
  }

  const btn = document.querySelector<HTMLElement>('[data-cashback-btn]');

  if (!btn) {
    // Fallback ancorado na largura real da sidebar (layoutStore.sideBar.width).
    // Sidebar fechada ≈ 70px; aberta ≈ 280px. Sem a largura ainda (store
    // não inicializou), usa 70px como mínimo seguro.
    const sidebar = layoutStore.sideBar.width || 70;

    globalCashbackStyle.value = {
      position: 'fixed',
      left: sidebar + 20 + 'px',
      top: '40%',
    };

    if (retries > 0 && showGlobalCashbackModal.value) {
      setTimeout(() => computeGlobalCashbackStyle(retries - 1), 50);
    }

    return;
  }

  const rect = btn.getBoundingClientRect();
  const menuWidth = 360;
  const offset = 40;
  let left = rect.left + rect.width + offset;

  if (left + menuWidth > window.innerWidth) {
    left = rect.left - menuWidth - offset;
  }

  globalCashbackStyle.value = {
    position: 'fixed',
    top: rect.top - 170 + 'px',
    left: left + 10 + 'px',
  };
}

// Único ponto de cálculo da âncora: dispara sempre que o modal abre,
// quando a sidebar muda de largura (abre/fecha) ou quando alterna entre
// desktop e mobile. Evita double-compute fazendo as outras chamadas
// (openGlobalCashbackFromRoute, watch shouldOpenCashback) só setarem o
// `showGlobalCashbackModal` — este watch reage e recalcula.
watch(
  [showGlobalCashbackModal, () => layoutStore.sideBar.width, isDesktop],
  () => {
    if (showGlobalCashbackModal.value) {
      nextTick(() => computeGlobalCashbackStyle());
    }
  },
);

// Reage ao estado global setado por outros componentes (ex: MenuHorizontal)
watch(shouldOpenCashback, (v) => {
  if (v) {
    showGlobalCashbackModal.value = true;
    consumeOpenRequest();
  }
});

function handleCashbackSelect(url: string) {
  showGlobalCashbackModal.value = false;
  navigateTo(url);
}

function handleCashbackClose() {
  showGlobalCashbackModal.value = false;
}

// Abre o modal e limpa a URL. A posição é calculada pelo watch que
// observa `showGlobalCashbackModal` (centralizado), não precisa
// chamar `computeGlobalCashbackStyle` daqui.
function openGlobalCashbackFromRoute() {
  if (hasRouteParam()) {
    showGlobalCashbackModal.value = true;

    // Limpa a URL
    const { open, ...rest } = route.query as Record<string, any>;
    const router = useRouter();

    router.replace({ path: route.path, query: rest, hash: '' }).catch(() => {});
  }
}

// Deep-link do hub Club Fidelidade (?open=clube-fidelidade). Abre o mesmo
// bottom-sheet global da badge via store (que já redireciona pro login se
// deslogado) e limpa a URL pra não reabrir em refresh/voltar. Gate na flag.
function openLoyaltyHubFromRoute() {
  if (!isClubFidelidadeEnabled.value) return;

  const q = route.query.open;
  const openParam = Array.isArray(q) ? q[0] : q;

  if (!openParam || !LOYALTY_HUB_QUERY_VALUES.includes(openParam)) return;

  loyaltyHubStore.open();

  const { open, ...rest } = route.query as Record<string, any>;
  const router = useRouter();

  router.replace({ path: route.path, query: rest, hash: '' }).catch(() => {});
}

// Controla se a sidebar deve ser carregada
// Desktop: sempre carrega
// Mobile: só carrega quando menu abrir pela primeira vez
const shouldLoadSidebar = ref(isDesktop.value);

// Controla se o footer deve ser carregado (via Intersection Observer apenas no mobile)
// SSR: sempre true para garantir que crawlers (Google/Bing) recebam o
// HTML completo do footer (links institucionais, CNPJ, compliance) —
// crítico para SEO em site YMYL.
const shouldLoadFooter = ref(import.meta.server || isDesktop.value);
const footerPlaceholder = ref<HTMLElement | null>(null);

watch(menuOpen, (isOpen) => {
  if (isOpen && !shouldLoadSidebar.value) {
    shouldLoadSidebar.value = true;
  }
});

// Scroll-lock do body via contador de referência compartilhado
// (useBodyScrollLock): menu mobile e cashback usam a mesma fonte de verdade
// que os modais (depósito, saque, Giratudo), pra não brigarem pelo overflow do
// body quando abrem juntos ou em sequência. Gate mobile/tablet mantém o
// comportamento anterior (a razão do removeProperty está no composable).
const isMenuScrollLocked = computed(
  () => isLayoutMobileOrTablet.value && menuOpen.value,
);

// Cashback trava em todas as larguras (no desktop também é overlay, não só o
// blur do conteúdo) — diferente do menu, que é overlay só no mobile/tablet.
const isCashbackScrollLocked = computed(() => isCashbackMenuOpen.value);

useBodyScrollLock(isMenuScrollLocked);
useBodyScrollLock(isCashbackScrollLocked);

const route = useRoute();

const hasGameSlug = computed<boolean>(() => {
  // @ts-ignore
  return !!route.params.game;
});

const currentPageClassName = computed<string>(() => {
  return route.name || '';
});

const isRoletasPage = computed(() => {
  return layoutStore.isDarkTheme;
});

useHead({
  bodyAttrs: {
    class: computed(() => {
      return [appNameLower()];
    }),
  },
  link: [
    {
      rel: 'canonical',
      href: 'https://' + appUrl() + route.path,
    },
  ],
});

onMounted(() => {
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');

  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', '#040C26');
  }

  // Verifica se deve abrir cashback a partir da URL (?open=cashback).
  // O fluxo via CasinoItems (sidebar) depende do template ref do botão e
  // pode falhar silenciosamente no desktop quando a sidebar lazy-load
  // ainda não posicionou o botão. Centralizar aqui garante que o modal
  // abra independentemente do estado da sidebar.
  openGlobalCashbackFromRoute();
  openLoyaltyHubFromRoute();

  // Intersection Observer para carregar footer quando usuário rolar perto (apenas mobile)
  if (!isDesktop.value) {
    nextTick(() => {
      const placeholder = footerPlaceholder.value;

      if (placeholder) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (entries[0]?.isIntersecting) {
              shouldLoadFooter.value = true;
              observer.disconnect();
            }
          },
          { rootMargin: '600px' },
        );

        observer.observe(placeholder);
      }
    });
  }
});

// Watch específico no query param pra disparar só quando relevante
// (não em toda navegação).
watch(
  () => route.query.open,
  (v) => {
    if (v === 'cashback') openGlobalCashbackFromRoute();
    else if (typeof v === 'string' && LOYALTY_HUB_QUERY_VALUES.includes(v)) {
      openLoyaltyHubFromRoute();
    }
  },
);
</script>

<template>
  <div
    id="divPageLayout"
    :class="[
      $style.pageLayout,
      currentPageClassName,
      { 'bg-black': isRoletasPage },
    ]"
    style="position: relative"
  >
    <div
      id="divPageHeaderWrapper"
      :class="[$style.headerWrapper, { 'bg-black': isRoletasPage }]"
    >
      <WidgetsTopBarUpToFiveItems v-if="isTopbarUpToFiveItemsEnabled" />
      <WidgetsTopBarSimple v-else />

      <Header :is-roletas-page="isRoletasPage" />
    </div>

    <WidgetsPullToRefreshIndicator
      :is-reloading="isReloading"
      :is-triggered="isTriggered"
      :max-display="maxDisplay"
      :pull-distance="pullDistance"
    />

    <SectionsMenuHorizontalApostatudo
      v-if="!isSportsPage"
      :class="[
        layoutStore.isDarkTheme ? 'bg-black' : 'bg-header-bg',
        'rounded-b-[10px] pb-[12px] pt-[8px] lg:hidden',
      ]"
    />

    <!-- Row principal: sidebar + conteúdo lado a lado no desktop.
         No mobile a sidebar é overlay (position: fixed) e sai do fluxo,
         então o contentWrapper continua ocupando 100% da largura. -->
    <div :class="$style.mainRow">
      <Sidebar
        v-if="!userCanOnlyWithdraw && shouldLoadSidebar"
        :is-roletas-page="isRoletasPage"
      />

      <WidgetsGeoLocationPermission />

      <div
        :class="[
          $style.contentWrapper,
          { [$style.withoutSidebar]: userCanOnlyWithdraw },
          { 'bg-black': isRoletasPage },
          { [String($style.cashbackBlur)]: isDesktop && isCashbackMenuOpen },
        ]"
        style="position: relative"
      >
        <!-- Overlay escuro para fade, agora só no contentWrapper -->
        <div
          v-if="isDesktop && isCashbackMenuOpen"
          class="cashbackFadeOverlay"
          style="
            position: absolute;
            inset: 0;
            z-index: 20;
            pointer-events: none;
          "
        ></div>
        <div
          :class="[
            $style.content,
            hasGameSlug ? (isMobile ? 'z-[40]' : 'z-[10]') : 'z-10',
            isSportsPage ? 'min-w-full p-0' : '',
            isPokerPage ? 'min-w-full p-0' : '',
            { 'bg-black': isRoletasPage },
          ]"
        >
          <slot />
        </div>

        <WidgetsBackToTop />

        <UserHeaderInfosBar v-if="!isSportsPage" />
      </div>
    </div>

    <!-- Footer full-width: vive fora do mainRow (flex-row sidebar+content)
         para ocupar a horizontal inteira no desktop, conforme Figma v2. -->
    <div
      ref="footerPlaceholder"
      :class="$style.footerWrapper"
      :style="
        casinoStore.hasMoreGamesToLoad
          ? 'display: none'
          : !shouldLoadFooter
            ? 'min-height: 1px'
            : ''
      "
    >
      <Footer
        v-if="
          shouldLoadFooter && !casinoStore.hasMoreGamesToLoad && !isSportsPage
        "
        :is-roletas-page="isRoletasPage"
      />
    </div>

    <WidgetsMobileMenuBarV2 />

    <!-- Modais e widgets críticos: apenas client-side para melhorar LCP/FCP -->
    <ClientOnly>
      <WidgetsCookieConsents />

      <WidgetsInstallAppPopup v-if="!isBot" />

      <WidgetsSupportButton />

      <WidgetsPwaUpdate />

      <BaseModalGlobal />

      <BaseModalVerification />

      <BaseModalLegalAge ref="legalAgeModal" />

      <UserAccountSecurityModalPassword />

      <ApostatudoGiratudoModal />

      <RouletteGame />

      <LoyaltyHub v-if="isClubFidelidadeEnabled" />

      <!-- Preview de jogo mobile (slide-up antes de abrir o iframe) -->
      <CasinoGameMobilePreview v-if="isLayoutMobileOrTablet" />

      <!-- Cashback modal global: mobile usa bottom-sheet (CashbackMenu
           trata via isDesktop interno). Desktop usa o mesmo dropdown
           ancorado ao botão da sidebar via [data-cashback-btn] — fluxo
           que substitui o ?open=cashback do CasinoItems, que falhava
           silenciosamente quando a sidebar ainda estava em lazy-load. -->
      <CashbackMenu
        v-if="showGlobalCashbackModal"
        :is-open="showGlobalCashbackModal"
        :style="globalCashbackStyle"
        @close="handleCashbackClose"
        @select-item="handleCashbackSelect"
      />
    </ClientOnly>
  </div>
</template>

<style module lang="postcss">
.pageLayout {
  @apply relative flex min-h-dvh flex-col;

  @media print {
    background-color: #fff !important;
  }

  .headerWrapper {
    @apply sticky top-0 z-[90];
  }

  .contentWrapper {
    @apply w-full;
    /* No desktop o contentWrapper vive dentro do flex-row (.mainRow)
       e ocupa o espaço restante (flex: 1). Não reservamos mais
       padding-left, pois a sidebar agora é inline. */
    @media (min-width: 1024px) {
      flex: 1 1 0;
      min-width: 0;
    }

    &.withoutSidebar {
      @apply lg:pl-0;
    }

    @media print {
      background-color: #fff !important;
    }

    .content {
      @apply container mx-auto max-w-[690px] lg:max-w-[1440px] z-10 px-4 md:px-0 lg:px-4 pt-4 lg:pt-6 xl:pt-8;

      @media print {
        background-color: #fff !important;
      }
    }
  }

  /* Row principal: sidebar inline + content lado a lado (desktop).
     No mobile a sidebar é fixed/overlay, então este row se comporta
     como um simples bloco e o contentWrapper ocupa 100%. */
  .mainRow {
    @apply w-full;
    @media (min-width: 1024px) {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
    }
  }

  /* Footer full-width: irmão do mainRow. Ocupa a horizontal inteira
     no desktop por viver fora do flex-row sidebar+content. */
  .footerWrapper {
    @apply w-full bg-bg-primary relative;
  }
}

/* Overlay escuro para fade junto com o blur */
.cashbackFadeOverlay {
  background: rgba(0, 0, 0, 0.25);
  border-radius: inherit;
  transition: background 0.2s;
} /* Blur aplicado ao layout quando o menu de cashback está aberto no
desktop */
.cashbackBlur {
  filter: blur(5px) !important;
  transition: filter 0.2s;
}
</style>
